import { IPatchUserCommentDto, IPostUserCommentDto, IUserComment, IUserResponse, TAdminMainPageInfo, TTakeInquiryRequest, TInquiryUpdateRequest, TTakeAllInquiriesOfTypeRequest, TCreateBulkReportsRequest } from "../types/auth.types";
import { api } from "./api";
import {
  IGenderOption,
  TAddMessageRequest,
  TAllUserQuestionnairesData,
  TGetMultipleQuestionnairesByUserIdAndCycleRequest,
  TGetMultipleQuestionnairesByUserIdAndCycleResponse,
  TGetUsersByTherapistIdResponse,
  TQuestionsOptionsData,
  TInsertAllAnswersRequest,
  TAddTherapistRequest,
  TAddUserRequest,
  TUpdateUserRequest,
  TRRMListEntry,
  TExportQuestionnaireRequest,
  TExportTherapistActionRequest,
  TExportReportResponse,
  TInsertAllAnswersQuestionsResponse,
  TUpdateTherapistsUsersConnectionsRequest,
  TUpdateTherapistsUsersConnectionsResponse,
  TTherapistsQuestionsReportsResponse,
  TTherapistsQuestionsReportsFilters,
  TGetQuestionnaireAnswersForEditResponse,
  TTherapistAvailability,
  TTherapist,
  TUpdateUserBalanceResponse,
  TUpdateUserBalanceRequest,
  IPendingReportResponce,
  TAddUserResponse,
  TClinicalFindingsResponse,
  mapClinicalFindingsFromApi,
  TClientUploadedFile,
  TAgentSipCredentials,
  TTherapistQuestionnaireForAutoFill,
} from "../types/user.types";
import { DeviceConfig, GetStaffDevicesResponse, StaffDevice } from "../types/device.types";
import {
  IFaq,
  IGetAllCombosForSearchUsersResponse,
  IGetUserEntitlementsResponse,
  IGetUserResponse,
  IGetUsersByCompanyIdWithRecommendsResponse,
  TDefaultReportDataResponse,
  TGetUsersRequest,
  TQuestionReportComment,
  TQuestionReportCommentResponse,
  TSetPasswordRequest,
  TUserByGuid,
} from "../types/users.types";
import {
  Report,
  TSpecialization,
  TTherapistSpecialization,
} from "../../types/types";
import { IResponse } from "../types";

/** An .xlsx template download: the file plus the name the server asked for. */
type TTemplateDownload = { blob: Blob; filename: string | null };

/**
 * Reads the file name from a Content-Disposition header (`filename="x"` and
 * `filename*=UTF-8''x`). The directory component is stripped so a malformed
 * header cannot steer where the browser saves the file; null means the caller
 * should fall back to its own default name.
 */
const parseContentDispositionFilename = (
  header: string | null
): string | null => {
  const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match) return null;

  let name = match[1].trim();
  try {
    name = decodeURIComponent(name);
  } catch {
    // not valid percent-encoding - keep the raw value
  }
  return name.split(/[\\/]/).pop()?.trim() || null;
};

const readTemplateResponse = async (
  response: Response
): Promise<TTemplateDownload> => ({
  blob: await response.blob(),
  filename: parseContentDispositionFilename(
    response.headers.get("Content-Disposition")
  ),
});

export const userApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUser: build.query<IUserResponse, void>({
      query: () => ({
        url: `/user`,
        method: "GET",
      }),
    }),
    getUserEntitlements: build.query<IGetUserEntitlementsResponse, void>({
      query: () => ({
        url: `/user/me/entitlements`,
        method: "GET",
      }),
    }),
    getAllUsers: build.mutation<
      { Users: IGetUserResponse[] },
      { companyId: number; id?: string | number }
    >({
      query: (body) => ({
        url: `user/Users_DML/1`,
        method: "POST",
        body,
      }),
    }),
    addUser: build.mutation<
      TAddUserResponse,
      {
        body: TAddTherapistRequest | TAddUserRequest;
        companyId: number | string;
      }
    >({
      query: ({ body, companyId }) => ({
        url: `Admintech/Admin_Users_DML/2/${companyId}`,
        method: "POST",
        body,
      }),
      // invalidatesTags: ["user"],
    }),
    /**
     * Activate (1) / deactivate (0) a user. Dedicated endpoint — the body carries the status and
     * nothing else, so the change can never be lost inside a partial profile update the way it was
     * when the toggle went through Admin_Users_DML/2.
     */
    updateUserStatus: build.mutation<
      { success: boolean; guid: string; statusId: number },
      { guid: string; statusId: number }
    >({
      query: ({ guid, statusId }) => ({
        url: `user/${guid}/status`,
        method: "PATCH",
        body: { statusId },
      }),
    }),
    /** Permanent delete: same request body shape as Admin_Users_DML/2 (without Password, Roles, Id). POST …/4/{companyId} */
    permanentDeleteClientUser: build.mutation<
      TAddUserResponse,
      {
        companyId: number;
        body: (TAddTherapistRequest | TAddUserRequest) & { deleteClinicalData?: boolean };
      }
    >({
      query: ({ companyId, body }) => ({
        url: `Admintech/Admin_Users_DML/4/${companyId}`,
        method: "POST",
        body,
      }),
    }),
    getAllQuestionnairesByUserId: build.query<
      TAllUserQuestionnairesData,
      { guid: string | number }
    >({
      query: ({ guid }) => ({
        url: `Questions/GetQuestionnairesByUsersId/${guid}`,
        method: "GET",
      }),
      providesTags: ["questionnaires"],
    }),
    // Questionnaires the caller's company exposes to staff — the action report switcher.
    getTherapistQuestionnairesForAutoFill: build.query<
      TTherapistQuestionnaireForAutoFill[],
      void
    >({
      query: () => ({
        url: `Questions/GetTherapistQuestionnairesForAutoFill`,
        method: "GET",
      }),
    }),
    addMessageToScore: build.mutation<boolean, TAddMessageRequest>({
      query: (answers) => ({
        url: `/Score/Score_AddMessageToScore`,
        method: "POST",
        body: JSON.stringify(answers),
        responseHandler: "text",
      }),
    }),
    getMultipleQuestionnairesByUserIdAndCycle: build.query<
      TGetMultipleQuestionnairesByUserIdAndCycleResponse,
      TGetMultipleQuestionnairesByUserIdAndCycleRequest
    >({
      query: ({ UsersGuid, Cycle, QuestionnaireCompaniesId }) => ({
        url: `Questions/GetMultiplesReportsByUserId/${QuestionnaireCompaniesId}/${UsersGuid}/${Cycle}`,
        method: "GET",
      }),
    }),
    getRRMData: build.query<
      TGetMultipleQuestionnairesByUserIdAndCycleResponse,
      void
    >({
      query: () => ({
        url: `Questions/GetMultiplesReportsByUserId`,
        method: "GET",
      }),
    }),
    getReportQuestionsAndOptionsByQuestionnaireId: build.query<
      TQuestionsOptionsData[],
      number
    >({
      query: (questionnaireId) => ({
        url: `Questions/GetAllQuestionsAndOptionsByQuestionnaireId/${questionnaireId}`,
        method: "GET",
      }),
      providesTags: (result, error, questionnaireId) => [
        { type: "QuestionnaireMap", id: String(questionnaireId) },
      ],
    }),
    updateQuestionnaireLayout: build.mutation<
      any,
      {
        questionnaireId: number;
        nodes: { id: string }[];
        edges: { source: string; target: string; sourceHandle: string }[];
      }
    >({
      query: (body) => ({
        url: "Questions/UpdateQuestionnaireLayout",
        method: "POST",
        body: body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "QuestionnaireMap", id: String(arg.questionnaireId) },
      ],
    }),
    insertAllAnswersQuestions: build.mutation<
      TInsertAllAnswersQuestionsResponse,
      {
        answers: TInsertAllAnswersRequest[];
        questionnaireCompaniesId?: number;
        appointmentsId?: number;
        questionnaireCompaniesScoreUsersId?: number;
        targetUserGuid?: string;
      }
    >({
      query: ({
        answers,
        questionnaireCompaniesId = 1,
        appointmentsId = 0,
        targetUserGuid,
        questionnaireCompaniesScoreUsersId,
      }) => ({
        url: `/Questions/InsertAllAnswersQuestions/${questionnaireCompaniesId}/${appointmentsId}/${questionnaireCompaniesScoreUsersId ?? 0}?targetUserGuid=${targetUserGuid ?? ""}`,
        method: "POST",
        body: JSON.stringify(answers),
      }),
      invalidatesTags: (result, error, { questionnaireCompaniesId }) => [
        "user",
        "questionnaires",
        { type: "QuestionnaireMap", id: questionnaireCompaniesId },
      ],
    }),
    getAllGenders: build.query<IGenderOption[], void>({
      query: () => ({
        url: `user/getAllGenders`,
        method: "GET",
      }),
    }),
    getAllCombosForSearchUsers: build.query<
      IGetAllCombosForSearchUsersResponse,
      number | string
    >({
      query: (companyId) => ({
        url: `user/GetAllCombosForSearchUsers/${companyId}`,
        method: "GET",
      }),
    }),
    getUsersByCompanyIdWithRecommends: build.query<
      IGetUsersByCompanyIdWithRecommendsResponse,
      TGetUsersRequest
    >({
      query: ({
        companyIds,
        fakeCompanyIds = [0],
        roleIds = [0],
        recommendsIds = [0],
        startDate = 0,
        endDate = 0,
        // 🆕 חדש:
        hasTreatmentProgram = null,
        fundingSourceIds = [0],
        treatmentTypeIds = [0],
        therapistGuids = [],
        userGuids = [],
        specializationIds = [0],
        isMinimal = false,
      }) => ({
        url: `user/GetUsersByCompanyIdsRoleIdsRecommendsIdsLists/${startDate}/${endDate}`,
        method: "POST",
        body: {
          CompanyIds: companyIds,
          FakeCompanyIds: fakeCompanyIds,
          RoleIds: roleIds,
          RecommendsIds: recommendsIds,
          // 🆕 חדש:
          HasTreatmentProgram: hasTreatmentProgram,
          FundingSourceIds: fundingSourceIds,
          TreatmentTypeIds: treatmentTypeIds,
          TherapistGuids: therapistGuids,
          UserGuids: userGuids,
          SpecializationIds: specializationIds,
          IsMinimal: isMinimal,
        },
      }),
      providesTags: ["user"],
    }),
    changePassword: build.mutation< //for personal password
      { success: boolean },
      { currentPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/Admintech/account/change-password',
        method: 'POST',
        body,
      }),
    }),
    adminResetUserPassword: build.mutation< //for clients
      { success: boolean },
      { targetUserGuid: string; newPassword: string }
    >({
      query: ({ targetUserGuid, newPassword }) => ({
        url: `/Admintech/admin/users/${encodeURIComponent(targetUserGuid)}/reset-password`,
        method: 'POST',
        body: { newPassword },
      }),
    }), getDefaultReportData: build.query<
      TDefaultReportDataResponse,
      string | number
    >({
      query: (userGuid) => ({
        url: `Therapists/Therapist_GetDefaultReport/${userGuid}`,
        method: "GET",
      }),
    }),
    getCommentsToRequest: build.mutation<
      TQuestionReportCommentResponse[],
      TQuestionReportComment
    >({
      query: (body) => ({
        url: `Therapists/Therapist_PostCommentsToRequest/1`,
        method: "POST",
        body,
      }),
    }),
    addOrUpdateFaqs: build.mutation<
      IFaq[],
      { faqs: IFaq[]; Type: number; CompanyId: number }
    >({
      query: ({ faqs, Type, CompanyId }) => ({
        url: `Admintech/Admin_Faq_DML/${Type}/${CompanyId}`,
        method: "POST",
        body: faqs,
      }),
    }),
    getFaqs: build.query<IFaq[], number>({
      query: (companyId) => ({
        url: `Admintech/Admin_Faq_GetByCompany/${companyId}`,
        method: "GET",
      }),
    }),
    therapistsSpecializationsDML: build.mutation<
      TSpecialization[],
      { list: TSpecialization[]; type: number; companyId: number }
    >({
      query: ({ list, type, companyId }) => ({
        url: `Admintech/Admin_TherapistsSpecializations_DML/${type}/${companyId}`,
        method: "POST",
        body: list,
      }),
    }),
    therapistsSpecializations2TherapistsDML: build.mutation<
      TTherapistSpecialization[],
      {
        list: TTherapistSpecialization[];
        type: number;
        usersGuid: string;
      }
    >({
      query: ({ list, type, usersGuid }) => ({
        url: `Admintech/Admin_TherapistsSpecializations2Therapists_DML/${type}/${usersGuid}`,
        method: "POST",
        body: list,
      }),
    }),
    /**
     * Paycall SIP credentials for one agent.
     * type: 1 = select, 2 = insert/update, 3 = delete (soft). Always returns the
     * agent's active rows, so callers drive their state from the response.
     */
    agentSipCredentialsDML: build.mutation<
      TAgentSipCredentials[],
      { list: TAgentSipCredentials[]; type: number; usersGuid: string }
    >({
      query: ({ list, type, usersGuid }) => ({
        url: `Admintech/Admin_AgentSipCredentials_DML/${type}/${usersGuid}`,
        method: "POST",
        body: list,
      }),
    }),
    getRRMList: build.mutation<
      TRRMListEntry[],
      { type: number; companyId: number }
    >({
      query: ({ type, companyId }) => ({
        url: `Admintech/Admin_CompanyGeneralRRM_DML/${type}/${companyId}`,
        method: "POST",
        body: [{}],
      }),
    }),
    getUserByGuid: build.query<TUserByGuid, string>({
      query: (Guid) => ({
        url: `user/User_GetUserByGuid/${Guid}`,
        method: "GET",
      }),
    }),
    uploadUsers: build.mutation<
      {
        message: string;
        errorsFound: number;
        errors: string[];
        createdCount?: number;
        createdByRole?: Record<string, number>;
      },
      FormData
    >({
      query: (formData) => ({
        url: `Admintech/Admin_UploadUsers_DML`,
        method: "POST",
        body: formData,
        //headers: { "X-Is-File-Upload": "true" },
      }),
      invalidatesTags: ["user"],
    }),
    updateNextQuestion: build.mutation<
      void,
      {
        id: number;
        type: "question" | "option";
        nextQuestionId: number | null;
        questionnaireId: number;
      }
    >({
      query: (updatePayload) => ({
        url: `Questions/UpdateNextQuestionId`,
        method: "POST",
        body: {
          id: updatePayload.id,
          type: updatePayload.type,
          nextQuestionId: updatePayload.nextQuestionId,
        },
      }),
      invalidatesTags: (result, error, { questionnaireId }) => [
        { type: "QuestionnaireMap", id: questionnaireId },
      ],
    }),
    addExercisesToUser: build.mutation<
      any[],
      { type: number; usersGuid: string; body?: number[] }
    >({
      query: ({ type, usersGuid, body = [] }) => ({
        url: `user/userExercises_DML/${type}/${usersGuid}`,
        method: "POST",
        body,
      }),
    }),
    getReportsByUser: build.query<Report[], string>({
      query: (guid) => ({
        url: `/Report/${guid}`,
        method: "GET",
      }),
    }),
    sendMeetingReportByEmail: build.mutation<
      IResponse,
      { reportId: number; email: string }
    >({
      query: (body) => ({
        url: "/PostReports/sendMeetingReportByEmail",
        method: "POST",
        body: body,
      }),
    }),
    // @deprecated Use updateUser instead - this endpoint will be removed in future versions
    updateUserProtocol: build.mutation<
      any,
      { userId: string; protocolId: number | null }
    >({
      query: ({ userId, protocolId }) => ({
        url: `Admintech/Admin_Users_Protocol/${userId}`,
        method: "PATCH",
        body: { protocolId },
      }),
    }),
    // updateUser: build.mutation<
    //   {
    //     isExistInAnotherCompany?: string | null;
    //     success: boolean;
    //     questionnaireCompaniesHeadersId?: number | null;
    //     user?: IGetUserResponse;
    //   },
    //   TUpdateUserRequest & { companyId: number }
    // >({
    //   query: ({ companyId, ...body }) => ({
    //     url: `Admintech/Admin_Users_DML/2/${companyId}`,
    //     method: "POST",
    //     body: body, // companyId לא נכלל ב-body, רק ב-URL
    //   }),
    // }),
    deleteUserAvatar: build.mutation<
      boolean,
      { guid: string }
    >({
      query: ({ guid }) => ({
        url: `user/${guid}/avatar`,
        method: "DELETE",
      }),
      invalidatesTags: ["user"],
    }),
    getExcelImportTemplate: build.query<TTemplateDownload, void>({
      query: () => ({
        url: `Admintech/Admin_DownloadUsersTemplate_DML`,
        responseHandler: readTemplateResponse,
      }),
      keepUnusedDataFor: 0,
    }),
    getPatientsImportTemplate: build.query<TTemplateDownload, void>({
      query: () => ({
        url: `Admintech/Admin_DownloadPatientsTemplate_DML`,
        responseHandler: readTemplateResponse,
      }),
      keepUnusedDataFor: 0,
    }),
    // Export Reports Endpoints
    exportQuestionnaireToPdf: build.query<
      TExportReportResponse,
      TExportQuestionnaireRequest
    >({
      query: ({
        userId,
        questionnaireCompaniesId,
        questionnaireId,
        cycle,
        language = "he",
        userRole,
      }) => {
        const params = new URLSearchParams({
          userId: userId.toString(),
          questionnaireCompaniesId: questionnaireCompaniesId.toString(),
          questionnaireId: questionnaireId.toString(),
          cycle: cycle.toString(),
          language: language,
        });
        if (userRole !== undefined) {
          params.append("userRole", userRole.toString());
        }
        return {
          url: `Reports/export/questionnaire/pdf?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    exportQuestionnaireToExcel: build.query<
      TExportReportResponse,
      TExportQuestionnaireRequest
    >({
      query: ({
        userId,
        questionnaireCompaniesId,
        questionnaireId,
        cycle,
        language = "he",
        userRole,
      }) => {
        const params = new URLSearchParams({
          userId: userId.toString(),
          questionnaireCompaniesId: questionnaireCompaniesId.toString(),
          questionnaireId: questionnaireId.toString(),
          cycle: cycle.toString(),
          language: language,
        });
        if (userRole !== undefined) {
          params.append("userRole", userRole.toString());
        }
        return {
          url: `Reports/export/questionnaire/excel?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    exportTherapistActionToPdf: build.query<
      TExportReportResponse,
      TExportTherapistActionRequest
    >({
      query: ({
        appointmentId,
        userId,
        therapistId,
        cycle,
        questionnaireId,
        questionnaireCompaniesId,
        language = "he",
        userRole,
      }) => {
        const params = new URLSearchParams({
          appointmentId: appointmentId.toString(),
          userId: userId.toString(),
          therapistId: therapistId.toString(),
          cycle: cycle.toString(),
          questionnaireId: questionnaireId.toString(),
          questionnaireCompaniesId: questionnaireCompaniesId.toString(),
          language: language,
        });
        if (userRole !== undefined) {
          params.append("userRole", userRole.toString());
        }
        return {
          url: `Reports/export/therapist-action/pdf?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    exportTherapistActionToExcel: build.query<
      TExportReportResponse,
      TExportTherapistActionRequest
    >({
      query: ({
        appointmentId,
        userId,
        therapistId,
        cycle,
        questionnaireId,
        questionnaireCompaniesId,
        language = "he",
        userRole,
      }) => {
        const params = new URLSearchParams({
          appointmentId: appointmentId.toString(),
          userId: userId.toString(),
          therapistId: therapistId.toString(),
          cycle: cycle.toString(),
          questionnaireId: questionnaireId.toString(),
          questionnaireCompaniesId: questionnaireCompaniesId.toString(),
          language: language,
        });
        if (userRole !== undefined) {
          params.append("userRole", userRole.toString());
        }
        return {
          url: `Reports/export/therapist-action/excel?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    getTherapistsByCompany: build.query<TTherapist[], void>({
      query: () => ({
        url: `user/GetTherapistsByCompany`,
        method: "GET",
      }),
    }),
    /**
     * Every device in the company — the board's columns, the management screen, and the picker
     * when booking all read from here, because a device belongs to the company and not to the
     * technician operating it.
     *
     * Passing the appointment window marks each device available or busy, so the picker offers
     * only machines that can actually take the appointment.
     */
    getCompanyDevices: build.query<
      StaffDevice[],
      | void
      | {
          date?: string;
          startTime?: string;
          endTime?: string;
          excludeAppointmentId?: number;
          includeInactive?: boolean;
        }
    >({
      query: (arg) => ({
        url: `user/GetCompanyDevices`,
        method: "GET",
        params: Object.fromEntries(
          Object.entries(arg ?? {}).filter(
            ([, value]) => value !== undefined && value !== "",
          ),
        ),
      }),
      transformResponse: (response: GetStaffDevicesResponse) => response?.devices ?? [],
      providesTags: ["CompanyDevices"],
    }),

    /** Creates a company device. Name, subsidiary, room and capabilities — no technician. */
    createDevice: build.mutation<
      { success: boolean; deviceContactId: number },
      { name: string; fakeCompanyId?: number | null; deviceConfig: DeviceConfig }
    >({
      query: (body) => ({ url: `Devices`, method: "POST", body }),
      invalidatesTags: ["CompanyDevices"],
    }),

    updateDevice: build.mutation<
      { success: boolean },
      { deviceId: number; name: string; fakeCompanyId?: number | null; deviceConfig: DeviceConfig }
    >({
      query: ({ deviceId, ...body }) => ({ url: `Devices/${deviceId}`, method: "PUT", body }),
      invalidatesTags: ["CompanyDevices"],
    }),

    /** Retires a device. Refused while it still has appointments ahead of it. */
    deactivateDevice: build.mutation<{ success: boolean }, number>({
      query: (deviceId) => ({ url: `Devices/${deviceId}`, method: "DELETE" }),
      invalidatesTags: ["CompanyDevices"],
    }),

    /**
     * Passing the appointment window marks each device available or busy, so the picker can offer
     * only devices that can actually take the appointment. Without a window everything comes back
     * available; the save path guards regardless.
     */
    getStaffDevices: build.query<
      GetStaffDevicesResponse,
      | string
      | {
          therapistGuid: string;
          date?: string;
          startTime?: string;
          endTime?: string;
          excludeAppointmentId?: number;
        }
    >({
      query: (arg) => {
        const { therapistGuid, ...window } =
          typeof arg === "string" ? { therapistGuid: arg } : arg;

        return {
          url: `user/GetStaffDevices/${therapistGuid}`,
          method: "GET",
          params: Object.fromEntries(
            Object.entries(window).filter(([, value]) => value !== undefined && value !== ""),
          ),
        };
      },
    }),
    getStaffByCompany: build.query<TTherapist[], void>({
      query: () => ({
        url: `user/GetStaffByCompany`,
        method: "GET",
      }),
    }),
    updateTherapistsUsersConnections: build.mutation<
      TUpdateTherapistsUsersConnectionsResponse,
      { body: TUpdateTherapistsUsersConnectionsRequest; type: number }
    >({
      query: ({ body, type }) => ({
        url: `/Therapists/Therapist_ManageUserConnections/${type}`, //1- add 2- remove
        method: "POST",
        body: body,
      }),
    }),
    getTherapistsQuestionsReports: build.query<
      TTherapistsQuestionsReportsResponse,
      TTherapistsQuestionsReportsFilters | void
    >({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters) {
          if (filters.therapistName) params.append("therapistName", filters.therapistName);
          if (filters.userName) params.append("userName", filters.userName);
          if (filters.questionnaireName) params.append("questionnaireName", filters.questionnaireName);
          if (filters.statusId) params.append("statusId", filters.statusId);
          if (filters.filled) params.append("filled", filters.filled);
          if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
          if (filters.dateTo) params.append("dateTo", filters.dateTo);
        }
        const query = params.toString();
        return {
          url: `questions/therapistQuestionsReports${query ? `?${query}` : ""}`,
          method: "GET",
        };
      },
    }),
    getTherapistUnreportedAppointments: build.query<
      TTherapistsQuestionsReportsResponse,
      TTherapistsQuestionsReportsFilters
    >({
      query: (body) => ({
        url: "Appointments/TherapistUnreportedAppointments",
        method: "POST",
        body,
      }),
      providesTags: ["UnreportedAppointments"],
    }),
    getQuestionnaireAnswersForEdit: build.query<
      TGetQuestionnaireAnswersForEditResponse,
      number
    >({
      query: (questionnaireCompaniesScoreUsersId) => ({
        url: `Questions/GetQuestionnaireAnswersForEdit/${questionnaireCompaniesScoreUsersId}`,
        method: "GET",
      }),
    }),
    getTherapistAvailability: build.query<
      TTherapistAvailability[],
      { CompanyId: number; guid: string }
    >({
      query: ({ CompanyId, guid }) => ({
        url: `/Therapists/TherapistsAvailability_DML/1/${guid}`,
        method: "POST",
        body: [{ companyid: CompanyId }],
      }),
    }),
    getAllTherapistsAvailability: build.query<TTherapistAvailability[], void>({
      query: () => ({
        url: `/Therapists/GetAllTherapistsAvailability`,
        method: "GET",
      }),
    }),
    updateUserBalance: build.mutation<TUpdateUserBalanceResponse, TUpdateUserBalanceRequest>({
      query: (body) => ({
        url: 'Admintech/Admin_UpdateUserBalance',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ["user", "UserPayments"],
    }),
    getPendingReports: build.query<IPendingReportResponce, number>({
      query: () => ({
        url: `Appointments/PendingReports/Admin`,
        method: "GET",
      }),
    }),
    updateUser: build.mutation<
      IUserResponse,
      { guid: string; body: { op: string; path: string; value: any }[] }
    >({
      query: ({ guid, body }) => ({
        url: `user/${guid}`,
        method: "PATCH",
        body: JSON.stringify(body),
      }),
      invalidatesTags: ["user"],
    }),
    updateTherapistInfo: build.mutation<
      IResponse,
      { guid: string; about?: string; video?: string }
    >({
      query: ({ guid, about, video }) => ({
        url: `user/updateAbout/${guid}`,
        method: "POST",
        body: { about, video },
      }),
      invalidatesTags: ["user"],
    }),
    getUserComments: build.query<IUserComment[], string>({
      query: (userGuid) => `user/GetUserComments/${userGuid}`,
      providesTags: (result, _err, userGuid) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: "UserComments" as const, id })),
            { type: "UserComments", id: `LIST-${userGuid}` },
          ]
          : [{ type: "UserComments", id: `LIST-${userGuid}` }],
    }),
    getUserAdminComments: build.query<IUserComment[], string>({
      query: (userGuid) => `user/GetUserAdminComments/${userGuid}`,
      providesTags: (result, _err, userGuid) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: "UserComments" as const, id })),
            { type: "UserAdminComments", id: `LIST-${userGuid}` },
          ]
          : [{ type: "UserAdminComments", id: `LIST-${userGuid}` }],
    }),
    postUserComment: build.mutation<
      Pick<IUserComment, "id" | "comment" | "createdOn">,
      IPostUserCommentDto
    >({
      query: (body) => ({
        url: "user/PostUserComment",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _err, dto) => [
        { type: "UserComments", id: `LIST-${dto.usersGuid}` },
      ],
    }),
    postUserAdminComment: build.mutation<
      Pick<IUserComment, "id" | "comment" | "createdOn">,
      IPostUserCommentDto
    >({
      query: (body) => ({
        url: "user/PostUserAdminComment",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _err, dto) => [
        { type: "UserAdminComments", id: `LIST-${dto.usersGuid}` },
      ],
    }),
    patchUserComment: build.mutation<
      Pick<IUserComment, "id" | "comment" | "createdOn">,
      { id: number; body: IPatchUserCommentDto }
    >({
      query: ({ id, body }) => ({
        url: `user/PatchUserComment/${id}`,
        method: "PATCH",
        body,
      }),
      // Invalidate the specific comment so any list containing it refetches
      invalidatesTags: (_result, _err, { id }) => [
        { type: "UserComments", id },
      ],
    }),
    patchUserAdminComment: build.mutation<
      Pick<IUserComment, "id" | "comment" | "createdOn">,
      { id: number; body: IPatchUserCommentDto }
    >({
      query: ({ id, body }) => ({
        url: `user/PatchUserAdminComment/${id}`,
        method: "PATCH",
        body,
      }),
      // Invalidate the specific comment so any list containing it refetches
      invalidatesTags: (_result, _err, { id }) => [
        { type: "UserAdminComments", id },
      ],
    }),
    getMainPageInfo: build.query<TAdminMainPageInfo, void>({
      query: () => ({
        url: `/Therapists/Admin_GetUserInsertQuestionnaire`,
        method: "GET",
      }),
    }),
    takeInquiry: build.mutation<IResponse, TTakeInquiryRequest>({
      query: (body) => ({
        url: `Therapists/Therapist_TakeUserRequest`,
        method: "POST",
        body,
      }),
    }),
    takeAllInquiriesOfType: build.mutation<
      IResponse,
      TTakeAllInquiriesOfTypeRequest
    >({
      query: (body) => ({
        url: `Therapists/Therapist_SyncUserInquiriesByType`,
        method: "POST",
        body,
      }),
    }),
    postInquiryUpdate: build.mutation<string, TInquiryUpdateRequest>({
      query: (body) => ({
        url: `/Therapists/Therapist_PostCommentsToRequest/2`,
        method: "POST",
        body,
        responseHandler: "text",
      }),
    }),
    createBulkReports: build.mutation<string, TCreateBulkReportsRequest>({
      query: (body) => ({
        url: `/Appointments/CreateBulkReports`,
        method: "POST",
        body,
        responseHandler: "text",
      }),
      invalidatesTags: ["UnreportedAppointments"],
    }),
    uploadFilesForClient: build.mutation<
      { success: boolean; urls: string[] },
      { patientUserGuid: string; formData: FormData }
    >({
      query: ({ patientUserGuid, formData }) => ({
        url: `Files/AdminUploadForClient/${patientUserGuid}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["ClientFiles"],
    }),
    /** Subjects offered when uploading a patient document — company defaults plus its own. */
    getFileSubjects: build.query<string[], void>({
      query: () => ({
        url: "Files/GetFileSubjects",
        method: "GET",
      }),
      transformResponse: (response: { success: boolean; subjects: string[] }) =>
        response?.subjects ?? [],
      providesTags: ["FileSubjects"],
    }),
    getClientFiles: build.query<TClientUploadedFile[], string>({
      query: (patientUserGuid) => ({
        url: `Files/GetClientFiles/${patientUserGuid}`,
        method: "GET",
      }),
      providesTags: ["ClientFiles"],
    }),
    adminDeleteClientFile: build.mutation<void, number>({
      query: (uploadedFileId) => ({
        url: `Files/AdminDeleteClientFile/${uploadedFileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ClientFiles"],
    }),
    getClinicalFindings: build.query<TClinicalFindingsResponse, string>({
      query: (userGuid) => ({
        url: `/MeetingRecords/clinical-findings/${userGuid}`,
        method: "GET",
      }),
      transformResponse: (response: Record<string, unknown>) =>
        mapClinicalFindingsFromApi(response ?? {}),
      // Findings are written in the background after a recording finishes processing. With the
      // default 60s cache, reopening a recording (or the patient page) soon after could show none.
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useLazyGetUserQuery,
  useGetUserEntitlementsQuery,
  useLazyGetUserByGuidQuery,
  useGetUserQuery,
  useGetAllUsersMutation,
  useGetAllGendersQuery,
  useLazyGetAllQuestionnairesByUserIdQuery,
  useGetTherapistQuestionnairesForAutoFillQuery,
  useAddMessageToScoreMutation,
  useGetAllQuestionnairesByUserIdQuery,
  useLazyGetMultipleQuestionnairesByUserIdAndCycleQuery,
  useGetReportQuestionsAndOptionsByQuestionnaireIdQuery,
  useLazyGetReportQuestionsAndOptionsByQuestionnaireIdQuery,
  useInsertAllAnswersQuestionsMutation,
  useLazyGetAllCombosForSearchUsersQuery,
  useLazyGetUsersByCompanyIdWithRecommendsQuery,
  useAddUserMutation,
  useUpdateUserStatusMutation,
  usePermanentDeleteClientUserMutation,
  useLazyGetDefaultReportDataQuery,
  useGetDefaultReportDataQuery,
  useGetCommentsToRequestMutation,
  useAddOrUpdateFaqsMutation,
  useGetFaqsQuery,
  useGetRRMListMutation,
  useUploadUsersMutation,
  useGetRRMDataQuery,
  useTherapistsSpecializationsDMLMutation,
  useUpdateNextQuestionMutation,
  useTherapistsSpecializations2TherapistsDMLMutation,
  useAgentSipCredentialsDMLMutation,
  useUpdateQuestionnaireLayoutMutation,
  useAddExercisesToUserMutation,
  useGetReportsByUserQuery,
  useSendMeetingReportByEmailMutation,
  useUpdateUserProtocolMutation,
  // useUpdateUserMutation,
  useDeleteUserAvatarMutation,
  useLazyGetExcelImportTemplateQuery,
  useLazyGetPatientsImportTemplateQuery,
  useLazyExportQuestionnaireToPdfQuery,
  useLazyExportQuestionnaireToExcelQuery,
  useLazyExportTherapistActionToPdfQuery,
  useLazyExportTherapistActionToExcelQuery,
  useGetTherapistsByCompanyQuery,
  useLazyGetTherapistsByCompanyQuery,
  useGetCompanyDevicesQuery,
  useLazyGetCompanyDevicesQuery,
  useCreateDeviceMutation,
  useUpdateDeviceMutation,
  useDeactivateDeviceMutation,
  useGetStaffDevicesQuery,
  useLazyGetStaffDevicesQuery,
  useGetStaffByCompanyQuery,
  useUpdateTherapistsUsersConnectionsMutation,
  useGetTherapistsQuestionsReportsQuery,
  useLazyGetQuestionnaireAnswersForEditQuery,
  useLazyGetTherapistAvailabilityQuery,
  useLazyGetAllTherapistsAvailabilityQuery,
  useUpdateUserBalanceMutation,
  useGetPendingReportsQuery,
  useUpdateUserMutation,
  useUpdateTherapistInfoMutation,
  useChangePasswordMutation,
  useAdminResetUserPasswordMutation,
  useGetUserCommentsQuery,
  usePostUserCommentMutation,
  usePatchUserCommentMutation,
  useGetUserAdminCommentsQuery,
  usePostUserAdminCommentMutation,
  usePatchUserAdminCommentMutation,
  useGetMainPageInfoQuery,
  useTakeInquiryMutation,
  usePostInquiryUpdateMutation,
  useTakeAllInquiriesOfTypeMutation,
  useGetTherapistUnreportedAppointmentsQuery,
  useCreateBulkReportsMutation,
  useUploadFilesForClientMutation,
  useGetFileSubjectsQuery,
  useGetClientFilesQuery,
  useAdminDeleteClientFileMutation,
  useGetClinicalFindingsQuery,
} = userApi;
